// The single source of truth for mapping between camera frame pixels and displayed
// element coordinates. Covers: object-fit: cover, digital zoom (CSS scale around
// center), and front-camera mirroring. Most bug-prone math in the app (spec §5) —
// keep every coordinate conversion going through here, and keep it unit-tested.

import type { NormRect, Point, Rect } from '../types';

export interface ViewParams {
  /** camera frame size in pixels */
  frameW: number;
  frameH: number;
  /** displayed element size in CSS pixels */
  elemW: number;
  elemH: number;
  /** digital zoom factor (>= 1); preview is CSS-scaled by this around center */
  zoom: number;
  /** front camera preview mirroring */
  mirrored: boolean;
}

/** Uniform scale + offset of the cover-fitted, zoomed frame inside the element. */
function coverScale(p: ViewParams): { s: number; dx: number; dy: number } {
  const s = Math.max(p.elemW / p.frameW, p.elemH / p.frameH) * p.zoom;
  return { s, dx: p.elemW / 2 - (s * p.frameW) / 2, dy: p.elemH / 2 - (s * p.frameH) / 2 };
}

export function frameToDisplay(pt: Point, p: ViewParams): Point {
  const { s, dx, dy } = coverScale(p);
  const x = pt.x * s + dx;
  return { x: p.mirrored ? p.elemW - x : x, y: pt.y * s + dy };
}

export function displayToFrame(pt: Point, p: ViewParams): Point {
  const { s, dx, dy } = coverScale(p);
  const x = p.mirrored ? p.elemW - pt.x : pt.x;
  return { x: (x - dx) / s, y: (pt.y - dy) / s };
}

export function frameRectToDisplay(r: Rect, p: ViewParams): Rect {
  const a = frameToDisplay({ x: r.x, y: r.y }, p);
  const b = frameToDisplay({ x: r.x + r.w, y: r.y + r.h }, p);
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(b.x - a.x),
    h: Math.abs(b.y - a.y),
  };
}

export function displayRectToFrame(r: Rect, p: ViewParams): Rect {
  const a = displayToFrame({ x: r.x, y: r.y }, p);
  const b = displayToFrame({ x: r.x + r.w, y: r.y + r.h }, p);
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(b.x - a.x),
    h: Math.abs(b.y - a.y),
  };
}

/**
 * The centered source-crop rect implied by a digital zoom factor,
 * normalized to the full frame. This is what the worker actually processes.
 */
export function zoomCropRect(zoom: number): NormRect {
  const w = 1 / zoom;
  return { x: (1 - w) / 2, y: (1 - w) / 2, w, h: w };
}

/** Convert a point in crop-local pixels back to full-frame pixels. */
export function cropToFrame(pt: Point, crop: NormRect, frameW: number, frameH: number): Point {
  return { x: pt.x + crop.x * frameW, y: pt.y + crop.y * frameH };
}

export function cropRectToFrame(r: Rect, crop: NormRect, frameW: number, frameH: number): Rect {
  const o = cropToFrame({ x: r.x, y: r.y }, crop, frameW, frameH);
  return { x: o.x, y: o.y, w: r.w, h: r.h };
}

/** Normalize a frame-pixel rect against frame dimensions, clamped to [0,1]. */
export function normalizeRect(r: Rect, frameW: number, frameH: number): NormRect {
  const x = Math.max(0, r.x / frameW);
  const y = Math.max(0, r.y / frameH);
  return {
    x,
    y,
    w: Math.min(1 - x, r.w / frameW),
    h: Math.min(1 - y, r.h / frameH),
  };
}
